/**
 * 사이드바 상단 워크스페이스 스위처 (Notion / Slack 식).
 *
 * 클릭 → 드롭다운 → 본인 소속 조직 목록 + "새 워크스페이스 만들기" 버튼.
 * 선택 시 useWorkspaceStore.selectedOrgId 갱신 → 각 페이지가 자동 필터링.
 *
 * 조직 1개만 있으면 (개인 워크스페이스만) 드롭다운 없이 그냥 표시 — 클릭 무반응.
 */
import { useEffect, useRef, useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Building2, Check, ChevronsUpDown, Plus, User } from "lucide-react";
import { CreateOrganizationDialog } from "@/components/create-organization-dialog";
import { cn } from "@/lib/cn";
import {
  listOrganizations,
  type OrganizationWithRole,
} from "@/lib/console-api";
import { useWorkspaceStore } from "@/stores/workspace-store";

export function WorkspaceSwitcher(): JSX.Element | null {
  const queryClient = useQueryClient();
  const selectedOrgId = useWorkspaceStore((s) => s.selectedOrgId);
  const setSelectedOrgId = useWorkspaceStore((s) => s.setSelectedOrgId);
  const reconcile = useWorkspaceStore((s) => s.reconcile);
  const [open, setOpen] = useState(false);
  const [createOpen, setCreateOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);

  const orgsQuery = useQuery({
    queryKey: ["organizations"],
    queryFn: listOrganizations,
  });

  const orgs = orgsQuery.data ?? [];

  // 조직 목록 변경 시 reconcile — 저장된 선택이 무효하면 첫 번째로 fallback.
  useEffect(() => {
    if (orgsQuery.isSuccess) reconcile(orgs);
  }, [orgsQuery.isSuccess, orgs, reconcile]);

  // 외부 클릭 시 닫기
  useEffect(() => {
    if (!open) return;
    const onClick = (e: MouseEvent): void => {
      if (
        containerRef.current &&
        !containerRef.current.contains(e.target as Node)
      ) {
        setOpen(false);
      }
    };
    const onKey = (e: KeyboardEvent): void => {
      if (e.key === "Escape") setOpen(false);
    };
    window.addEventListener("mousedown", onClick);
    window.addEventListener("keydown", onKey);
    return () => {
      window.removeEventListener("mousedown", onClick);
      window.removeEventListener("keydown", onKey);
    };
  }, [open]);

  if (orgsQuery.isLoading || orgs.length === 0) {
    return (
      <div className="rounded-md border border-border bg-card px-3 py-2 text-sm text-muted-foreground">
        {orgsQuery.isLoading ? "워크스페이스 로딩…" : "조직 없음"}
      </div>
    );
  }

  const selected =
    orgs.find((o) => o.id === selectedOrgId) ?? orgs[0] ?? null;
  if (!selected) return null;

  return (
    <div ref={containerRef} className="relative">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className={cn(
          "flex w-full items-center gap-2 rounded-md border border-border bg-card px-3 py-2 text-left text-sm transition-colors hover:bg-accent/50",
          open && "bg-accent/50",
        )}
        aria-haspopup="listbox"
        aria-expanded={open}
      >
        <WorkspaceIcon plan={selected.plan} />
        <div className="min-w-0 flex-1">
          <p className="truncate font-medium">{selected.name}</p>
          <p className="truncate text-xs text-muted-foreground">
            {planLabel(selected.plan)} · {roleLabel(selected.role)}
          </p>
        </div>
        <ChevronsUpDown className="h-4 w-4 shrink-0 text-muted-foreground" />
      </button>

      {open && (
        <div className="absolute left-0 right-0 top-full z-[100] mt-1 overflow-hidden rounded-md border border-border bg-card shadow-xl">
          <ul role="listbox" className="max-h-72 overflow-y-auto py-1">
            {orgs.map((o) => (
              <li key={o.id}>
                <button
                  type="button"
                  role="option"
                  aria-selected={o.id === selected.id}
                  onClick={() => {
                    setSelectedOrgId(o.id);
                    setOpen(false);
                  }}
                  className={cn(
                    "flex w-full items-center gap-2 px-3 py-2 text-left text-sm transition-colors hover:bg-accent",
                    o.id === selected.id && "bg-accent/50",
                  )}
                >
                  <WorkspaceIcon plan={o.plan} />
                  <div className="min-w-0 flex-1">
                    <p className="truncate font-medium">{o.name}</p>
                    <p className="truncate text-xs text-muted-foreground">
                      {planLabel(o.plan)} · {roleLabel(o.role)}
                    </p>
                  </div>
                  {o.id === selected.id && (
                    <Check className="h-4 w-4 shrink-0 text-primary" />
                  )}
                </button>
              </li>
            ))}
          </ul>
          <div className="border-t border-border p-1">
            <button
              type="button"
              onClick={() => {
                setOpen(false);
                setCreateOpen(true);
              }}
              className="flex w-full items-center gap-2 rounded-sm px-3 py-2 text-left text-sm text-muted-foreground transition-colors hover:bg-accent hover:text-foreground"
            >
              <Plus className="h-4 w-4 shrink-0" />
              <span>새 워크스페이스 만들기</span>
            </button>
          </div>
        </div>
      )}

      <CreateOrganizationDialog
        open={createOpen}
        onClose={() => setCreateOpen(false)}
        onCreated={(org) => {
          // 생성 직후 새 워크스페이스로 자동 전환 + 캐시 갱신
          setSelectedOrgId(org.id);
          void queryClient.invalidateQueries({ queryKey: ["organizations"] });
        }}
      />
    </div>
  );
}

function WorkspaceIcon({ plan }: { plan: string }): JSX.Element {
  const Icon = plan === "PERSONAL" ? User : Building2;
  return (
    <div className="inline-flex h-8 w-8 shrink-0 items-center justify-center rounded-md bg-primary/10 text-primary">
      <Icon className="h-4 w-4" aria-hidden />
    </div>
  );
}

function planLabel(plan: OrganizationWithRole["plan"]): string {
  switch (plan) {
    case "PERSONAL":
      return "개인";
    case "TEAM":
      return "팀";
    case "ENTERPRISE":
      return "엔터프라이즈";
    default:
      return plan;
  }
}

function roleLabel(role: OrganizationWithRole["role"]): string {
  switch (role) {
    case "OWNER":
      return "소유자";
    case "ADMIN":
      return "관리자";
    case "MEMBER":
      return "멤버";
    case "VIEWER":
      return "뷰어";
    default:
      return role;
  }
}
